import React, { FC } from 'react';
import { BasicContainer, BodyTextLarge, FlexCol, FlexRow, H2Text, H3Text, H4Text, Loader } from '@minecraft.atoms';
import { Trans, useTranslation } from '@blocs.i18n';
import { LoadingSkeleton } from '@minecraft.loading-skeleton';
import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { FEATURE_FLAGS, useFeatureExperiment } from '../../../FeaturesAndExperiments';
import { priceFormatter } from '../../helpers/priceFormatter';
import { BillingCodes } from '../../enums/plans';
import { PlanType } from '../../usePlans';
import { UsePayment } from '../../types';
import { PromoCodeInput } from '../PromoCodeInput';
import { PaymentSummaryProps } from '../types';

export type SummarySectionProps = {
  selectedPlan?: PlanType;
  currentPurchase?: PaymentSummaryProps['currentPurchase'];
  currencyCode: PaymentSummaryProps['currencyCode'];
  countryCode: PaymentSummaryProps['countryCode'];
  onApplyCoupon?: UsePayment['applyCoupon'];
  applyCouponError?: UsePayment['applyCouponError'];
  couponLoading?: UsePayment['couponLoading'];
  onRemoveCoupon?: UsePayment['removeCoupon'];
  addPaymentLoading?: boolean;
  completePurchaseLoading?: boolean;
  purchaseableItems?: PaymentSummaryProps['purchaseableItems'];
  // Only the upgrade/checkout caller sets this; the registration caller never does, so it's a no-op there.
  enableAnnualRenewal?: boolean;
  // true (default): only show promo entry when the registration promo-code flag is on (registration's
  // original behavior). false: promo entry has always been unconditionally available on the upgrade
  // flow (the original PaymentSummary.tsx never checked this registration-specific flag).
  requireRegistrationPromoFlag?: boolean;
  // false (default): use registration's conditional (coupon/discount-aware) decimal formatting.
  // true: always show decimals (the upgrade flow's original PaymentSummary.tsx behavior, which
  // never passed noDecimal at all).
  forceDecimalPricing?: boolean;
};

const getLineItemPrice = ({
  currentPurchase,
  purchaseableItems,
  selectedPlan,
  appliedCouponCode,
}: {
  currentPurchase: SummarySectionProps['currentPurchase'];
  purchaseableItems: SummarySectionProps['purchaseableItems'];
  selectedPlan: SummarySectionProps['selectedPlan'];
  appliedCouponCode: string;
}): number => {
  if (purchaseableItems?.length > 0) {
    return Number(currentPurchase?.total ?? 0) + Number(currentPurchase?.discount ?? 0);
  }
  if (!appliedCouponCode) {
    return Number(
      selectedPlan.isTrialPeriodEnabled
        ? currentPurchase?.total
        : currentPurchase?.subscription?.plan.price ?? currentPurchase?.total ?? 0
    );
  }
  return Number(currentPurchase?.subscription?.plan.price ?? currentPurchase?.total ?? 0);
};

// Renders the order-total / promo-code portion only. Submit CTA + terms live in CTAWithTerms.
export const SummarySection: FC<SummarySectionProps> = ({
  selectedPlan,
  currentPurchase,
  currencyCode,
  countryCode,
  onApplyCoupon,
  applyCouponError,
  couponLoading,
  onRemoveCoupon,
  addPaymentLoading,
  completePurchaseLoading,
  purchaseableItems,
  enableAnnualRenewal,
  requireRegistrationPromoFlag = true,
  forceDecimalPricing = false,
}) => {
  const { t } = useTranslation();
  const registrationPromoCodeFlag = useFeatureExperiment(FEATURE_FLAGS.WEB_REGISTRATION_PROMO_CODE_REDEMPTION);

  // Wallet mode passes neither a plan nor a purchase — nothing to summarize.
  if (!selectedPlan && !currentPurchase) return null;

  const translationCode =
    selectedPlan?.translationCode ?? selectedPlan?.billingCode ?? currentPurchase?.subscription?.plan?.billingCode;
  const appliedCouponCode = currentPurchase?.coupon?.code;
  const isSelectedPlanRepRegDiscount = selectedPlan?.discountType === DiscountTypeEnum.ReppedRegistrationDiscount;
  const isFreePlanSelected = selectedPlan?.billingCode === BillingCodes.registration;
  const isCardlessTrial = selectedPlan?.isCardlessTrial;
  const noDecimal =
    !forceDecimalPricing && ((!appliedCouponCode && !isSelectedPlanRepRegDiscount) || isFreePlanSelected);
  const isLoading = addPaymentLoading || completePurchaseLoading || couponLoading;
  const hasCreditsApplied = currentPurchase?.creditsApplied > 0;
  const chargeableItems = purchaseableItems?.filter((item) => item?.price > 0);
  const displayPromoCodeInput =
    !isSelectedPlanRepRegDiscount &&
    (!requireRegistrationPromoFlag || registrationPromoCodeFlag) &&
    !isFreePlanSelected &&
    !isCardlessTrial &&
    !enableAnnualRenewal;

  const lineItemPrice = getLineItemPrice({ currentPurchase, selectedPlan, purchaseableItems, appliedCouponCode });

  return (
    <BasicContainer w="full" bgColor="listItem" padding={6} borderRadius="sm">
      {displayPromoCodeInput && (
        <>
          <PromoCodeInput
            onApplyCoupon={onApplyCoupon}
            onRemoveCoupon={onRemoveCoupon}
            appliedCouponCode={appliedCouponCode}
            applyCouponError={applyCouponError}
            couponLoading={couponLoading}
          />
          <hr />
        </>
      )}
      <Loader loading={isLoading} LoaderComponent={<LoadingSkeleton className="cn_w-full" height="1rem" />}>
        <FlexCol data-qa-id="paywall-payment-summary" w="full" gap="2">
          <FlexRow data-qa-id="paywall-membership-summary" w="full" justifyContent="space-between" alignItems="center">
            <FlexCol>
              <BodyTextLarge color="emphasis" fontWeight="semibold">
                <Trans
                  i18nKey={`common:paywall.success.orderSummary.${translationCode}`}
                  components={[<br />]}
                  values={{ count: chargeableItems?.length ?? 0 }}
                />
              </BodyTextLarge>
            </FlexCol>
            <H3Text color="emphasis" fontWeight="semibold">
              {priceFormatter({ price: lineItemPrice, currencyCode, countryCode, t, noDecimal })}
            </H3Text>
          </FlexRow>
          {hasCreditsApplied && (
            <FlexRow data-qa-id="paywall-payment-credits-applied" w="full" justifyContent="space-between">
              <BodyTextLarge color="emphasis" fontWeight="semibold">
                {t('common:membership.subscription.creditsApplied')}
              </BodyTextLarge>
              <BodyTextLarge color="emphasis" fontWeight="semibold">
                {priceFormatter({
                  price: -(currentPurchase?.creditsApplied ?? 0),
                  currencyCode,
                  countryCode,
                  t,
                  noDecimal,
                })}
              </BodyTextLarge>
            </FlexRow>
          )}
          {appliedCouponCode && (
            <FlexRow data-testid="coupon-container" w="full" justifyContent="space-between" alignItems="center">
              <FlexCol>
                <BodyTextLarge data-qa-id="applied-coupon-code" color="emphasis" fontWeight="semibold">
                  {appliedCouponCode}
                </BodyTextLarge>
                {currentPurchase?.coupon?.description && (
                  <BodyTextLarge data-qa-id="applied-coupon-code-description" color="emphasis">
                    {currentPurchase.coupon.description}
                  </BodyTextLarge>
                )}
                <BodyTextLarge color="emphasis" fontWeight="semibold">
                  <Trans
                    i18nKey="common:paywall.summary.promoCodeDiscount"
                    values={{
                      discountPercent: (currentPurchase.discount / currentPurchase?.subscription?.plan?.price) * 100,
                    }}
                  />
                </BodyTextLarge>
              </FlexCol>
              <H3Text color="emphasis" fontWeight="semibold" data-qa-id="applied-discount">
                -{priceFormatter({ price: Number(currentPurchase?.discount), currencyCode, countryCode, t })}
              </H3Text>
            </FlexRow>
          )}
        </FlexCol>
      </Loader>
      <hr />
      <Loader loading={isLoading} LoaderComponent={<LoadingSkeleton className="cn_w-full" height="1rem" />}>
        <FlexRow w="full" justifyContent="space-between">
          <H4Text color="emphasis" fontWeight="semibold">
            {t('common:paywall.summary.total')}
          </H4Text>
          <H2Text data-qa-id="payment-total" color="emphasis" as="p">
            {priceFormatter({ price: currentPurchase?.total ?? 0, currencyCode, countryCode, t, noDecimal })}
          </H2Text>
        </FlexRow>
      </Loader>
    </BasicContainer>
  );
};
